import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import { createAppTheme } from '../theme/theme';
import type { AppTheme, ThemeMode } from '../theme/theme';
import type { UserProfile } from '../../../libs/shared/types/account';
import { apiService } from '../services/apiService.js';

const STORAGE_KEY = 'ai_theme_preference';

type ThemeContextValue = {
  theme: AppTheme;
  mode: ThemeMode;
  toggleTheme: () => Promise<void>;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  syncWithUser: (user: UserProfile | null, authToken: string | null) => void;
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return 'dark';
  });

  const authTokenRef = useRef<string | null>(null);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  /**
   * Call this from App.js once the user profile is available.
   * Syncs the theme mode from the user's DB preference and stores the auth token
   * for future API calls.
   */
  const syncWithUser = useCallback(
    (user: UserProfile | null, authToken: string | null) => {
      authTokenRef.current = authToken;
      const pref = user?.theme_preference;
      if (pref === 'light' || pref === 'dark') {
        if (pref !== mode) {
          setMode(pref);
          localStorage.setItem(STORAGE_KEY, pref);
        }
      }
    },
    [mode],
  );

  const toggleTheme = useCallback(async () => {
    const newMode: ThemeMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
    if (authTokenRef.current) {
      try {
        await apiService.updateUserPreferences(authTokenRef.current, { theme_preference: newMode });
      } catch (err) {
        console.error('Failed to persist theme preference:', err);
      }
    }
  }, [mode]);

  const setThemeMode = useCallback(async (newMode: ThemeMode) => {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
    if (authTokenRef.current) {
      try {
        await apiService.updateUserPreferences(authTokenRef.current, { theme_preference: newMode });
      } catch (err) {
        console.error('Failed to persist theme preference:', err);
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      theme,
      mode,
      toggleTheme,
      setThemeMode,
      syncWithUser,
      isDark: mode === 'dark',
    }),
    [theme, mode, toggleTheme, setThemeMode, syncWithUser],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useAppTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      theme: createAppTheme('dark'),
      mode: 'dark',
      toggleTheme: async () => {},
      setThemeMode: async () => {},
      syncWithUser: () => {},
      isDark: true,
    };
  }
  return ctx;
};

export default ThemeContext;
